import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, withRouter } from 'react-router-dom';
import { featureCollection } from '@turf/helpers';
import { bbox } from '@turf/turf';
import { useFlags } from 'launchdarkly-react-client-sdk';
import mapboxgl, { GeolocateControl } from 'mapbox-gl';
import MapboxMap, { DrawControl } from 'components/MapboxMap';
import { debounce, flattenDeep } from 'lodash';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

import ShareIcon from 'assets/share.svg?react';
import DeleteModal from 'components/Modals/DeleteModal';
import { Context } from 'components/Store';
import { parseClientError } from 'helpers/errorHelpers';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import ClickableDiv from 'components/ClickableDiv';
import { useAuth } from 'utilities/base-auth';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { MAP_STYLE_TYPES } from 'helpers/constants';
import drawStyles from 'screens/Property/helpers/drawStyles';
import styleInjector from 'screens/Property/helpers/styleInjector';
import {
  DEFAULT_FLY_TO_OPTIONS,
  DEFAULT_ZOOM,
  UPDATE_TYPES,
  MAP_HISTORY_FEATURE_TYPES,
  MIN_ZOOM,
  MAX_ZOOM,
  USA_CENTER_COORDINATES
} from 'screens/Property/helpers/constants';
import {
  getFeatureCollectionsFromGeoJSONItems,
  handleShapeDelete,
  handleShapeSubtract,
  handleShapeSplit
} from 'screens/Property/helpers/mapApiHelpers';
import useExportPDFMapsActions from 'screens/Property/hooks/useExportPDFMapsActions';
import PropertyContext from '../../PropertyMapContext';
import MapKeyPressTool from '../MapKeyPressTool';
import PropertyFeatureLayer from '../PropertyFeatureLayer';
import GrisLayer, { GRIS_TYPES } from '../GrisLayer';
import useMapMouseListeners from './hooks/useMapMouseListeners';
import useDrawHandlers from './hooks/useDrawHandlers';
import PropertyPopup from '../PropertyPopup';
import DrawTools from './DrawTools';
import ObjectMarkerLabel from './ObjectMarkerLabel';
import ChangeSummary from './ChangeSummary';
import PrintableAreaBoundary from './PrintableAreaBoundary';
import MapControlButton from './MapControlButton';
import { calculateCorrectMapHeight } from './utilities/helpers';
import coordinatesGeocoder from './utilities/coordinatesGeocoder';
import { sortMapLayers } from './utilities/mapboxLayerHelpers';
import './map.css';
import { createRegex, editRegex } from '../constants';

const AccessToken = "pk.eyJ1Ijoic3RyaWRlcmVuZ2luZWVyaW5nIiwiYSI6ImNsdWs5ZG1vNzA2azYyam1scHljMjNyc3kifQ.YBj-8NtAHftqXroxqmbxXw";

const CardWrapper = ({ noCard = false, children }) => {
  const [{ isMassAssignerActive }] = useContext(Context);
  const mapHeight = calculateCorrectMapHeight(isMassAssignerActive);

  return noCard ? (
    <div className="relative w-full flex grow">{children}</div>
  ) : (
    <div className="shadow-md rounded-md">
      <div className="relative bg-white rounded-md">
        <div className="relative w-full" style={{ height: mapHeight }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const filterSelectedFeatures = features =>
  features.filter(
    ({ properties }) =>
      properties?.$layer === 'selected' && !(properties.$noEdit ?? false)
  );
const filterUnselectedFeatures = collection => {
  return featureCollection(
    collection.features.filter(
      ({ properties }) =>
        properties?.$layer !== 'selected' || !!properties.$noEdit
    )
  );
};
const filterExportPDFMapFeatures = (selectedFields, collection) => {
  const selectedCropZoneIds = flattenDeep(
    selectedFields.map(({ cropzones }) => cropzones.map(({ id }) => id))
  );
  return featureCollection(
    collection.features
      .filter(({ properties }) => {
        if (properties.$landType === 'cropzone') {
          return selectedCropZoneIds.includes(properties.id);
        }
        return true;
      })
      .map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          $layer:
            feature.properties.$landType === 'cropzone'
              ? 'focused'
              : feature.properties.$layer,
          sortKey:
            feature.properties.$landType === 'cropzone'
              ? selectedCropZoneIds.indexOf(feature.properties.id) + 1
              : 0
        }
      }))
  );
};
const markFeaturesSelected = (features, layerType = 'selected') =>
  features.map(({ properties, ...feature }) => ({
    ...feature,
    properties: { ...properties, $layer: layerType }
  }));

const isADrawingMode = mode => ['draw_polygon', 'drag_circle'].includes(mode);
const isASelectMode = mode => ['select_only', 'simple_select'].includes(mode);
const unallowedLayerd = layer => ['focused', 'selected'].includes(layer);

const propertyMapDefaultPropSetCurrentCoordinates = () => {};
const propertyMapDefaultPropGeoJSONCollection = {
  features: [],
  type: 'FeatureCollection'
};
const propertyMapDefaultPropHandleDrawCreate = () => {};
const propertyMapDefaultPropOnFit = () => {};
const propertyMapDefaultPropHandleDrawUpdate = () => {};
const propertyMapDefaultPropHandleDrawDelete = () => {};
const propertyMapDefaultPropSetZoom = () => {};
const PropertyMap = ({
  currentCoordinates = null,
  setCurrentCoordinates = propertyMapDefaultPropSetCurrentCoordinates,
  displayOnly = false,
  geoJSONCollection = propertyMapDefaultPropGeoJSONCollection,
  customStyles = null,
  handleDrawCreate = propertyMapDefaultPropHandleDrawCreate,
  handleDrawUpdate = propertyMapDefaultPropHandleDrawUpdate,
  handleDrawDelete = propertyMapDefaultPropHandleDrawDelete,
  liveUpdate = true,
  // markerOn,
  mode,
  modes,
  setMode,
  modeReset,
  zoom = DEFAULT_ZOOM,
  setZoom = propertyMapDefaultPropSetZoom,
  stretch = false,
  fieldsLoading,
  labelName = '',
  isCreate = false,
  showArchivedLabels = false,
  disableAutoFit = false,
  onFit = propertyMapDefaultPropOnFit
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [overlay, setOverlay] = useState(/** @type {string|null} */ (null));
  const [popup, setPopup] = useState(false);
  const [showSplitPopup, setShowSplitPopup] = useState(false);
  const { onMapMouseMoved, onMapMouseDown } = useMapMouseListeners(mode);
  const { pathname } = useLocation();
  const amplitude = useContext(AmplitudeContext);

  const [
    {
      organization,
      massiveChangeSummary,
      isMassAssignerActive,
      fieldsToMassiveAssign,
      isExportPDFMapsActive,
      exportPDFMapsStep,
      propertyMapFieldsConfig,
      mapLabelConfig,
      mapStyleImage,
      allFFTGeoJson
    },
    dispatch
  ] = useContext(Context);

  const { user } = useAuth();
  const flags = useFlags();
  const isStaticMap = isExportPDFMapsActive && exportPDFMapsStep === 1;

  const showMapType = () => {
    if (isStaticMap) return mapStyleImage;
    return MAP_STYLE_TYPES.satellite;
  };
  const mapStyle = showMapType();
  const { initiateExportFlow } = useExportPDFMapsActions(dispatch);

  const {
    selectedShapeIds,
    setSelectedShapeIds,
    newFeatures,
    mapChangeEvent,
    archiveMapHistoryFeature
  } = useContext(PropertyContext);

  const drawControlRef = useRef(null);
  const [drawReady, setDrawReady] = useState(false);
  const lastMapChangeEvent = useRef(new Event('init ref event'));
  const featureIdBeingSplit = useRef();
  const deletedFeatureIds = useRef([]);
  const mapRef = React.useRef(null);

  const onEditScreen = !displayOnly;

  const geocoder = useMemo(
    () =>
      new MapboxGeocoder({
        accessToken: AccessToken,
        collapsed: true,
        localGeocoder: coordinatesGeocoder,
        placeholder: 'ZIP, Address, Lat/Long',
        mapboxgl
      }),
    []
  );
  const geolocateControl = useMemo(
    () =>
      new GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true
      }),
    []
  );

  const isInPropertyLanding =
    pathname.includes('/property-landing') &&
    !editRegex.test(pathname) &&
    !createRegex.test(pathname);

  const showExportPDFMapsButton =
    !isExportPDFMapsActive &&
    !isMassAssignerActive &&
    isInPropertyLanding &&
    featureFlagRenderItem(
      flags.releaseFePropertyLandingBulkActions,
      flags.releaseFePropertyLandingBulkActionsByOrg,
      organization.id
    ) &&
    flags.experimentFeExportPdfMapsMapControlByUserId.includes(user.id);

  const isGeoJSONLabelEnabled = featureFlagRenderItem(
    flags.releaseFeGeoJsonLayerMapLabel,
    flags.releaseFeGeoJsonLayerMapLabelByOrg,
    organization.id
  );

  const geoJSONFeatureCollections = useMemo(
    () => getFeatureCollectionsFromGeoJSONItems(geoJSONCollection.features),
    [geoJSONCollection]
  );

  const showDrawControls = !displayOnly || mode === 'select_only';
  const displayOnlyGeoJSONCollection = useMemo(() => {
    const filteredFeatures = geoJSONCollection.features.filter(
      feature => feature.geometry
    );
    if (onEditScreen) {
      return filterUnselectedFeatures({
        ...geoJSONCollection,
        features: filteredFeatures
      });
    }
    if (isExportPDFMapsActive) {
      return filterExportPDFMapFeatures(fieldsToMassiveAssign, {
        ...geoJSONCollection,
        features: filteredFeatures
      });
    }
    return featureCollection(filteredFeatures);
  }, [
    onEditScreen,
    isExportPDFMapsActive,
    fieldsToMassiveAssign,
    geoJSONCollection
  ]);

  const inDisplayOnlyJSON = useCallback(
    feature =>
      displayOnlyGeoJSONCollection.features.find(
        ({ id: _id }) => _id === feature.id
      ),
    [displayOnlyGeoJSONCollection]
  );

  const flyToOptions = DEFAULT_FLY_TO_OPTIONS;

  const onStyleLoad = (mapReference, urlStyle) => {
    if (isStaticMap) {
      mapReference.scrollZoom.disable();
      mapReference.boxZoom.disable();
      mapReference.dragRotate.disable();
      mapReference.dragPan.disable();
      mapReference.keyboard.disable();
      mapReference.doubleClickZoom.disable();
      mapReference.touchZoomRotate.disable();
      styleInjector(mapReference, urlStyle);
    } else {
      // Runs again after every base-style switch; addControl does not dedupe.
      if (!mapReference.hasControl(geocoder)) {
        mapReference.addControl(geocoder, 'bottom-right');
      }
      if (!mapReference.hasControl(geolocateControl)) {
        mapReference.addControl(geolocateControl, 'bottom-right');
      }
    }

    setMapLoaded(true);
  };

  // does the heavy lifting of replacing a new feature with a new instance of the feature with proper id
  const processNewFeature = useCallback(
    (newFeature, callback) => {
      if (newFeature.id && liveUpdate) {
        // Delete the feature with autogenerated id so user can't update wrong feature
        drawControlRef.current.draw.delete(newFeature.id);
      }

      const callbackResults = callback(newFeature);

      if (liveUpdate) drawControlRef.current.draw.add(newFeature); // Insert the feature with the correct id

      return callbackResults;
    },
    [liveUpdate]
  );

  const { onDrawChanged } = useDrawHandlers(
    handleDrawCreate,
    handleDrawUpdate,
    processNewFeature,
    setDeleteModalOpen
  );

  const handleDelete = () => {
    const currentMode = drawControlRef.current.draw.getMode();
    if (currentMode === 'direct_select') {
      drawControlRef.current.draw.trash();
    } else if (currentMode === 'simple_select') {
      setDeleteModalOpen(true);
    }
  };

  const handlePolygonDelete = () => {
    const deletedIds = handleShapeDelete(drawControlRef.current);
    if (!deletedIds) {
      parseClientError(dispatch)('You must select a shape to delete');
    } else {
      handleDrawDelete(deletedIds);
      setSelectedShapeIds([]);
    }
    setDeleteModalOpen(false);
  };

  const handlePolygonUnselect = () => {
    const deletedIds = handleShapeDelete(drawControlRef.current);
    if (deletedIds) {
      handleDrawDelete(deletedIds);
      setSelectedShapeIds([]);
    }
    setDeleteModalOpen(false);
  };

  const handleCloseDeleteModal = () => setDeleteModalOpen(false);

  const handleSelectionChange = () => {
    setSelectedShapeIds(drawControlRef.current.draw.getSelectedIds());
  };

  const handleSubtract = async () => {
    const results = handleShapeSubtract(drawControlRef.current);

    if (!results) {
      parseClientError(dispatch)(
        'You must select a shape completely contained by the other selected shape'
      );
    } else {
      const { added, deleted } = results;
      // handleDrawDelete is optionally async - callers pass a promise-returning
      // handler, the default is a synchronous no-op. Promise.resolve hands back
      // a real promise either way, and returns the caller's own promise
      // untouched when there is one.
      await Promise.resolve(handleDrawDelete(deleted));
      setSelectedShapeIds([]);
      processNewFeature(added, newFeature => {
        handleDrawCreate([newFeature]);
      });
    }
  };

  const handleSplitStart = () => {
    // eslint-disable-next-line prefer-destructuring
    featureIdBeingSplit.current = selectedShapeIds[0];
    setMode('draw_line_string');
  };

  const handleSplit = async lineFeature => {
    const { added, deleted } = handleShapeSplit(
      drawControlRef.current,
      featureIdBeingSplit.current,
      lineFeature
    );

    if (!added) {
      parseClientError(dispatch)(
        'The split could not be performed as requested.'
      );
    } else {
      handleDrawDelete([featureIdBeingSplit.current]);
      deletedFeatureIds.current = deletedFeatureIds.current.concat(deleted);
      const addedFeatures = markFeaturesSelected(added.features);

      handleDrawCreate(addedFeatures, deleted);
    }
    featureIdBeingSplit.current = null;
    modeReset();
  };

  const handleImportShape = useCallback(
    ({ feature }) => {
      drawControlRef.current.draw.add(feature); // Insert the feature with the correct id
      handleDrawCreate(markFeaturesSelected([feature]));
    },
    [handleDrawCreate]
  );

  // An imported batch lands like a finished drawing: it becomes the selection
  // (the draw control does not select shapes added through its API, and an
  // unselected shape is left out of the save), and the map is fitted once to
  // everything drawn so the new shapes are seen next to what was already there.
  const settleImportedShapes = useCallback(
    importedIds => {
      setSelectedShapeIds(importedIds);
      const drawn = drawControlRef.current?.draw?.getAll?.();
      const map = mapRef.current;
      if (!map?.fitBounds || !drawn?.features?.length) return;
      const bounds = bbox(drawn);
      if (bounds.some(bound => !Number.isFinite(bound))) return;
      map.fitBounds(bounds, { padding: 160, animate: false });
    },
    [setSelectedShapeIds]
  );

  const handleCreate = draw => {
    const currentMode = drawControlRef.current.draw.getMode();

    if (currentMode === 'draw_line_string') {
      // only using the draw line mode for the split command
      const [lineFeature] = draw.features;
      handleSplit(lineFeature);
    } else {
      const tmpFeatures = onEditScreen
        ? markFeaturesSelected(draw.features)
        : draw.features;

      const mappedFeatures = tmpFeatures.map(ft => {
        if (ft.properties.isCircle) {
          const { $layer, center } = ft.properties;

          return {
            ...ft,
            properties: {
              $layer,
              center
            }
          };
        }

        return ft;
      });
      onDrawChanged(mappedFeatures, UPDATE_TYPES.CREATE);
      // modeReset();
    }
  };

  const canDraw = useMemo(
    () =>
      isCreate &&
      isASelectMode(mode) &&
      !overlay &&
      !geoJSONCollection.features.some(feature =>
        unallowedLayerd(feature.properties.$layer)
      ),
    [isCreate, overlay, mode, geoJSONCollection.features]
  );

  const showPopup = () => {
    if (canDraw) {
      setPopup(true);
    }
  };

  const cannotSplit = useMemo(
    () =>
      geoJSONCollection.features.some(feature =>
        unallowedLayerd(feature.properties.$layer)
      ) && selectedShapeIds.length !== 1,
    [geoJSONCollection.features, selectedShapeIds]
  );

  const showSplitToolPopup = hide => {
    if (cannotSplit && !hide) {
      setShowSplitPopup(true);
    } else {
      setShowSplitPopup(false);
    }
  };

  const onClickExportButton = () => {
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.Properties.accessPropertyMaps,
      {
        from: 'top right of map'
      }
    );
    initiateExportFlow();
  };

  useEffect(() => {
    sortMapLayers(mapRef);
  }, [propertyMapFieldsConfig, mapLabelConfig]);

  // The shapes marked for editing go into the draw control once it is on the
  // map and whenever they change. draw.add replaces by id, so re-running is
  // idempotent; this used to happen during render, on every render.
  useEffect(() => {
    if (
      !drawReady ||
      !drawControlRef.current ||
      !showDrawControls ||
      !onEditScreen
    ) {
      return;
    }
    geoJSONFeatureCollections.forEach(({ features = [] }) => {
      const filteredFeatures = filterSelectedFeatures(features)
        .filter(feature => feature.geometry)
        .map(feature => ({
          id: feature.properties.id,
          description: feature.properties.name,
          ...feature
        }));
      drawControlRef.current.draw.add(featureCollection(filteredFeatures));
    });
  }, [drawReady, geoJSONFeatureCollections, showDrawControls, onEditScreen]);

  // detects when changes are made to the includeOnMaps property in geoJSONCollection
  useEffect(() => {
    // We should pass only as a geoJSONCollection in the future as a prop to LandMap in this way
    if (
      !displayOnly &&
      geoJSONCollection.features.length &&
      drawControlRef.current
    ) {
      const drawnFeatures = drawControlRef.current.draw.getAll();
      geoJSONCollection.features.forEach(feature => {
        // do not include features that have been explicitly excluded or permanently deleted
        const includeOnMaps =
          (!feature.properties || feature.properties.includeOnMaps !== false) &&
          !deletedFeatureIds.current.includes(feature.id);
        const drawnFeature = drawnFeatures.features.find(drawn => {
          return drawn.id === feature.id;
        });

        if (includeOnMaps && !drawnFeature && !inDisplayOnlyJSON(feature)) {
          // add feature if not drawn but should be included on map
          drawControlRef.current.draw.add(feature);
        } else if (!includeOnMaps && drawnFeature) {
          // remove feature if drawn but should not be included on map
          drawControlRef.current.draw.delete(drawnFeature.id);
        }
      });
    }
  }, [geoJSONCollection, displayOnly, inDisplayOnlyJSON]);

  useEffect(() => {
    if (isASelectMode(mode) && mapLoaded && drawControlRef.current?.draw) {
      drawControlRef.current.draw.changeMode(mode, {
        featureIds: selectedShapeIds || []
      });
    }
  }, [mode, mapLoaded, selectedShapeIds]);

  useEffect(() => {
    if (
      !displayOnly &&
      mode !== 'select_only' &&
      mapLoaded &&
      drawControlRef.current?.draw &&
      drawControlRef.current.draw.getMode() !== mode
    ) {
      drawControlRef.current.draw.changeMode(mode);
    }
  }, [mode, mapLoaded, drawControlRef, displayOnly]);

  // process any new features added to the mapHistory, then archive it
  // mapChangeEvent only changes when new features are ready to be processed
  useEffect(() => {
    if (
      mapChangeEvent.timeStamp !== lastMapChangeEvent.current.timeStamp &&
      newFeatures.length > 0
    ) {
      lastMapChangeEvent.current = mapChangeEvent;
      const importedIds = [];
      newFeatures.forEach(newFeature => {
        if (newFeature.featureType === MAP_HISTORY_FEATURE_TYPES.IMPORT) {
          handleImportShape(newFeature);
          importedIds.push(newFeature.feature.id);
          archiveMapHistoryFeature(newFeature);
        }
        if (newFeature.featureType === MAP_HISTORY_FEATURE_TYPES.ADD) {
          const addedFeatures = markFeaturesSelected([newFeature.feature]);
          handleDrawCreate(addedFeatures);
          archiveMapHistoryFeature(newFeature);
        }
        if (newFeature.featureType === MAP_HISTORY_FEATURE_TYPES.REMOVE) {
          handleDrawDelete([newFeature.feature.id]);
          archiveMapHistoryFeature(newFeature);
        }
      });
      if (importedIds.length) settleImportedShapes(importedIds);
    }
  }, [
    mapChangeEvent,
    newFeatures,
    handleImportShape,
    settleImportedShapes,
    handleDrawCreate,
    handleDrawDelete,
    archiveMapHistoryFeature
  ]);

  const handleNewFeature = newShapes => {
    setSelectedShapeIds(newShapes.map(shape => shape.feature.id));
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.once) return undefined;
    // The adapter applies the style prop; this only waits for that load to
    // land. Only the latest request may complete: a second switch before the
    // previous styledata arrived would otherwise run the stale callback too,
    // and with the old style's URL.
    const handleStyleData = () => onStyleLoad(map, mapStyle);
    map.once('styledata', handleStyleData);
    return () => {
      map.off('styledata', handleStyleData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      sortMapLayers(mapRef);
    }, 450);

    return () => clearTimeout(timeoutId);
  }, [mapStyleImage]);

  useEffect(() => {
    if (!mapRef.current || displayOnly) return undefined;

    // Handler to force update after drag ends
    const handleDragEnd = debounce(() => {
      if (drawControlRef.current?.draw) {
        const currentMode = drawControlRef.current.draw.getMode();
        if (
          currentMode === 'direct_select' ||
          currentMode === 'simple_select'
        ) {
          const updatedFeatures = drawControlRef.current.draw.getAll().features;
          if (updatedFeatures.length) {
            onDrawChanged(updatedFeatures, UPDATE_TYPES.UPDATE);
          }
        }
      }
    }, 50);

    // Listen for mouseup and touchend (drag end) on the map container
    const mapContainer =
      mapRef.current &&
      mapRef.current.getContainer &&
      mapRef.current.getContainer();
    if (mapContainer) {
      mapContainer.addEventListener('mouseup', handleDragEnd);
      mapContainer.addEventListener('touchend', handleDragEnd);
      mapContainer.addEventListener('pointerup', handleDragEnd);
    }
    return () => {
      if (mapContainer) {
        mapContainer.removeEventListener('mouseup', handleDragEnd);
        mapContainer.removeEventListener('touchend', handleDragEnd);
        mapContainer.removeEventListener('pointerup', handleDragEnd);
      }
    };
  }, [mapRef, onDrawChanged, displayOnly]);

  const handleMouseUp = useMemo(
    () =>
      debounce(() => {
        if (canDraw) {
          setMode('draw_polygon');
        }
      }, 150),
    [canDraw, setMode]
  );

  const handlePointerStart = event => {
    if (!displayOnly && drawControlRef.current?.draw) {
      const currentMode = drawControlRef.current.draw.getMode();
      if (isADrawingMode(currentMode)) onMapMouseDown(event);
    }
  };

  const handleMouseMove = (_, event) => {
    if (!displayOnly && drawControlRef.current?.draw) {
      const currentMode = drawControlRef.current.draw.getMode();
      if (isADrawingMode(currentMode)) onMapMouseMoved(event, drawControlRef);
    }
  };

  return (
    <ClickableDiv
      data-testid="land-map"
      className={`property-map ${
        isStaticMap ? 'static-map cursor-pointer' : ''
      } ${stretch ? 'grow flex-col flex' : 'h-full'}`}
      onMouseEnter={() => showPopup()}
      onMouseLeave={() => setPopup(false)}
    >
      <CardWrapper noCard={stretch}>
        <PropertyPopup
          position="center"
          ariaLabel="Click to split"
          msg="To use the split tool, please select the shape you want to split on the map, one at a time"
          showPopup={showSplitPopup}
        />
        {popup && (
          <PropertyPopup
            position="center"
            ariaLabel="Click to draw"
            msg="Click to start drawing a shape"
            showPopup={popup}
          />
        )}
        {isMassAssignerActive && (
          <ChangeSummary massiveChangeSummary={massiveChangeSummary} />
        )}
        {showExportPDFMapsButton && (
          <MapControlButton
            data-testid="export-pdf-maps-map-control"
            className="absolute py-10px px-4"
            style={{ top: 72, right: 20 }}
            onClick={onClickExportButton}
          >
            <ShareIcon className="mr-1" />
            <span
              style={{ letterSpacing: '-0.105px' }}
              className="leading-5 ml-1px"
            >
              Export PDF Map
            </span>
          </MapControlButton>
        )}
        {!isStaticMap && (
          <DrawTools
            modeSelected={setMode}
            mode={mode}
            onDelete={handleDelete}
            onSubtract={handleSubtract}
            onSplit={handleSplitStart}
            setCurrentCoordinates={setCurrentCoordinates}
            setZoom={setZoom}
            overlay={overlay}
            setOverlay={setOverlay}
            features={geoJSONCollection.features}
            setSelectedShapeIds={setSelectedShapeIds}
            showSplitToolPopup={showSplitToolPopup}
          />
        )}
        {isExportPDFMapsActive && exportPDFMapsStep === 0 && (
          <PrintableAreaBoundary />
        )}
        <MapboxMap
          style={mapStyle}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          preserveDrawingBuffer
          zoom={zoom}
          center={currentCoordinates?.coordinates || USA_CENTER_COORDINATES}
          flyToOptions={flyToOptions}
          containerStyle={{
            height: '100%',
            width: '100%'
          }}
          onStyleLoad={map => {
            mapRef.current = map;
            onStyleLoad(map, mapStyle);
          }}
          onMouseMove={handleMouseMove}
          onMouseDown={(_, event) => handlePointerStart(event)}
          onMouseUp={handleMouseUp}
          onTouchStart={(_, event) => handlePointerStart(event)}
          onTouchEnd={handleMouseUp}
          // The zoom buttons step from this state, and only a fit to shapes
          // updated it, so on an empty map (or after a scroll zoom) their
          // first click jumped from a stale level. Keep it on the map's.
          onZoomEnd={map => {
            const current = map.getZoom();
            setZoom(previous =>
              previous?.[0] === current ? previous : [current]
            );
          }}
        >
          <PropertyFeatureLayer
            key={`map-feature-with-label:${isGeoJSONLabelEnabled}`}
            geoJSON={displayOnlyGeoJSONCollection}
            customStyles={customStyles}
            setZoom={setZoom}
            visible={displayOnly || onEditScreen}
            activeObjectType={labelName}
            isGeoJSONLabelEnabled={isGeoJSONLabelEnabled}
            currentCoordinates={currentCoordinates}
            allFFTGeoJson={allFFTGeoJson}
            showArchivedLabels={showArchivedLabels}
            disableAutoFit={disableAutoFit}
            onFit={onFit}
          />
          {overlay === 'CLU' && (
            <GrisLayer
              type={GRIS_TYPES.CLU}
              setZoom={setZoom}
              geoJSONCollection={geoJSONCollection}
              onChange={handleNewFeature}
              onDelete={handlePolygonUnselect}
            />
          )}
          {overlay === 'SAT' && (
            <GrisLayer
              type={GRIS_TYPES.SATELLITE}
              setZoom={setZoom}
              geoJSONCollection={geoJSONCollection}
              onChange={handleNewFeature}
              overlay={overlay}
              onDelete={handlePolygonUnselect}
            />
          )}

          {showDrawControls && (
            <DrawControl
              ref={drawControl => {
                drawControlRef.current = drawControl;
              }}
              // The handle lands after the render that first checked it, so
              // the initial shapes need a render of their own once the control
              // is really on the map.
              onReady={() => setDrawReady(true)}
              onRemoved={() => setDrawReady(false)}
              onDrawCreate={handleCreate}
              onDrawDelete={draw => {
                onDrawChanged(draw.features, UPDATE_TYPES.DELETE);
              }}
              onDrawUpdate={draw => {
                onDrawChanged(draw.features, UPDATE_TYPES.UPDATE);
              }}
              onDrawSelectionChange={handleSelectionChange}
              defaultMode="simple_select"
              displayControlsDefault={false}
              modes={modes}
              styles={drawStyles}
            />
          )}
          {!fieldsLoading && !isGeoJSONLabelEnabled && (
            <ObjectMarkerLabel
              geoCollection={geoJSONCollection}
              activeObjectType={labelName}
            />
          )}
          <MapKeyPressTool onDelete={handleDelete} setMode={setMode} />
        </MapboxMap>
        <DeleteModal
          open={deleteModalOpen && !displayOnly}
          itemType="Shape"
          onDelete={handlePolygonDelete}
          onCancel={handleCloseDeleteModal}
        />
      </CardWrapper>
    </ClickableDiv>
  );
};

CardWrapper.propTypes = {
  noCard: PropTypes.bool,
  children: PropTypes.node.isRequired
};

PropertyMap.propTypes = {
  currentCoordinates: PropTypes.shape({
    coordinates: PropTypes.arrayOf(PropTypes.number),
    userGeolocation: PropTypes.arrayOf(PropTypes.number)
  }),
  setCurrentCoordinates: PropTypes.func,
  displayOnly: PropTypes.bool,
  geoJSONCollection: PropTypes.shape({
    features: PropTypes.arrayOf(PropTypes.object),
    type: PropTypes.string
  }),
  customStyles: PropTypes.objectOf(
    PropTypes.shape({
      fillColor: PropTypes.string,
      fillOutlineColor: PropTypes.string
    })
  ),
  handleDrawCreate: PropTypes.func,
  handleDrawUpdate: PropTypes.func,
  handleDrawDelete: PropTypes.func,
  mode: PropTypes.string.isRequired,
  modeReset: PropTypes.func.isRequired,
  modes: PropTypes.objectOf(PropTypes.object).isRequired,
  setMode: PropTypes.func.isRequired,
  liveUpdate: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func
  }),
  zoom: PropTypes.arrayOf(PropTypes.number),
  setZoom: PropTypes.func,
  stretch: PropTypes.bool,
  labelName: PropTypes.string,
  fieldsLoading: PropTypes.bool.isRequired,
  isCreate: PropTypes.bool,
  showArchivedLabels: PropTypes.bool,
  disableAutoFit: PropTypes.bool,
  onFit: PropTypes.func
};

export default React.memo(withRouter(PropertyMap));
